import type { QueryClient, QueryExecuteOptions, QueryKey } from '@tanstack/react-query'

import { plainSymbol } from '@/features/trading-chart/api/chartMappers'
import {
  chartWarmCacheSchema,
  type ChartDatafeedConfig,
  type ChartSymbol,
  type ChartWarmCache,
} from '@/schemas/trading-chart'

/**
 * The chart's warm cache: the datafeed config and the symbols charted lately,
 * kept in localStorage so a repeat visit answers `onReady` and `resolveSymbol`
 * without waiting on mm-be (docs/trading-chart.md, "Warm cache").
 *
 * localStorage, because every chart opens in a tab of its own and starts with
 * an empty query cache. Only these two reads are kept: they are small, the
 * same for every member, and change only with a deploy or a listing. Never
 * bars (stale prices drawn as current), tokens or layouts.
 *
 * Every read and write is wrapped in try/catch, as lib/lastUsed.ts's are: a
 * blocked or full store costs the next visit its head start, never the chart.
 * The key carries a version, and a copy that no longer parses is ignored.
 */

const WARM_KEY = 'mm-chart-warm-v1'

/** Symbols kept, newest first. About half a kilobyte each. */
const MAX_SYMBOLS = 20

const EMPTY: ChartWarmCache = { symbols: {} }

const readStore = (): ChartWarmCache => {
  try {
    const raw = window.localStorage.getItem(WARM_KEY)
    if (raw === null) return EMPTY
    const parsed = chartWarmCacheSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : EMPTY
  } catch {
    // Blocked storage or a damaged copy: this visit simply reads from mm-be.
    return EMPTY
  }
}

const writeStore = (store: ChartWarmCache): void => {
  try {
    window.localStorage.setItem(WARM_KEY, JSON.stringify(store))
  } catch {
    // Blocked or full storage costs the next visit its head start, not this chart.
  }
}

export const readWarmConfig = (): ChartDatafeedConfig | undefined => readStore().config

export const readWarmSymbol = (symbol: string): ChartSymbol | undefined =>
  readStore().symbols[plainSymbol(symbol)]?.info

export const keepWarmConfig = (config: ChartDatafeedConfig): void => {
  writeStore({ ...readStore(), config })
}

/** Keeps a symbol mm-be just answered, or forgets one it no longer lists (`null`). */
export const keepWarmSymbol = (symbol: string, info: ChartSymbol | null): void => {
  const key = plainSymbol(symbol)
  const store = readStore()
  const entries = Object.entries(store.symbols).filter(([name]) => name !== key)
  if (info !== null) entries.push([key, { at: Date.now(), info }])
  entries.sort(([, a], [, b]) => b.at - a.at)
  writeStore({ ...store, symbols: Object.fromEntries(entries.slice(0, MAX_SYMBOLS)) })
}

/**
 * Starts a chart read that a repeat visit answers at once.
 *
 * When this tab has nothing cached under the key but an earlier visit kept a
 * copy, the copy goes into the cache, fresh as far as the chart can tell, so
 * the datafeed's own read of it resolves without waiting. The request goes out
 * anyway and replaces it: stale-while-revalidate. With no copy, or one already
 * cached here, this is a plain read. The promise is the request's.
 */
export function startWarmRead<TData, TKey extends QueryKey>(
  client: QueryClient,
  options: QueryExecuteOptions<TData, Error, TData, TData, TKey>,
  kept: TData | undefined,
): Promise<TData> {
  if (kept === undefined || client.getQueryData(options.queryKey) !== undefined) {
    return client.query(options)
  }
  client.setQueryData<TData>(options.queryKey, kept)
  return client.query({ ...options, staleTime: 0 })
}
